/**
 * Everything related to interaction firebase database and interaction tools.
 */

import { ElementType, MouseEvent, ReactNode, Ref } from 'react';

import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Id, Maybe, Optional } from '@vimeo/core/types/generic';

import { TVimeoApiConnectionExtended } from './api';
import { IFirebaseApiConfiguration } from './firebase';
import { IInteractionApplicationUser } from './interaction_room';

export type TInteractionHistoryConnection = TVimeoApiConnectionExtended<{
  id?: Id;
  streamingOn: number;
}>;

export interface IInteractionToolsMetadata {
  connections: {
    chatHistory: Array<TInteractionHistoryConnection>;
    questionsHistory: Array<TInteractionHistoryConnection>;
    pollsHistory: Array<TInteractionHistoryConnection>;
  };
}

export interface IFirebaseInteractionCredentials {
  config: IFirebaseApiConfiguration;
  appName: string;
  token: string;
  roomId: Id;
  roomHash: string;
  user: IInteractionApplicationUser;
  metadata?: IInteractionToolsMetadata;
}

export interface IInteractionBaseFirebaseConfig {
  appName: string;
  config: {
    apiKey: string;
    authDomain: string;
    databaseUrl: string;
    projectId: string;
    storageBucket: string;
  };
  jwt: string;
}

export interface IInteractionBaseConfig {
  roomId: Id;
  roomHash: string;
  firebase: IInteractionBaseFirebaseConfig;
  user: IInteractionApplicationUser;
  metadata?: IInteractionToolsMetadata;
}

export interface IInteractionApplicationConfig extends IInteractionBaseConfig {
  embed: {
    code: string;
    source: string;
  };
  settings: {
    accentColor: string;
    loginOptions: {
      canLoginAsGuest: boolean;
      canLoginAsVimeo: boolean;
    };
  };
  interaction: {
    url: string;
    title: string;
  };
  permission: {
    error: Optional<unknown>;
    privacy: string;
  };
}

export type IGuestInteractionApplicationConfig = IInteractionBaseConfig;

/**
 * Raw DB record containing message author information.
 */
export interface IChatMessageAuthorRecord {
  id: string;
  displayName?: string;
  avatarUrl?: string;
  isCreator?: boolean;
}

/**
 * Raw DB record containing message information.
 */
export interface IChatMessageRecord {
  contents: string;
  createdAt: number;
  user: IChatMessageAuthorRecord;
}

export interface IChatMessage {
  id: string;
  message: string;
  createdAt: number;
  senderId: string;
}

export interface IChatUser {
  id: string;
  avatarUrl: string;
  displayName: string;
  isCreator: boolean;
  isStaff: boolean;
}

/**
 * Transformed question author model.
 */
export interface IQnAQuestionAuthor {
  avatarUrl: string;
  displayName: string;
  id: string;
}

/**
 * Transformed question model based on firebase DB entry.
 */
export interface IQnAQuestion {
  id: string;
  anonymous: boolean;
  createdAt: number;
  archivedAt: number;
  state: EQuestionState;
  text: string;
  user: Optional<IQnAQuestionAuthor>;
  repliesCount?: Optional<number>;
  approved?: boolean;
  votesCount: number;
  isVotedByMe: boolean;
}

/**
 * Transformed question reply model based on firebase DB entry.
 */
export interface IQnAQuestionReply {
  id: string;
  questionId: string;
  createdAt: number;
  text: string;
  user: IQnAQuestionAuthor;
  votes?: Record<string, any>;
}

/**
 * Transformed question session.
 */
export interface IQnASession {
  createdAt: number;
  id: string;
  startedAt: number;
  isQnaModerated: boolean;
}

/**
 * Firebase DB entry of QNA session.
 */
export interface IQnASessionRecord {
  createdAt: number;
  id: string;
  startedAt: number;
  isQnaModerated: boolean;
  questions: Record<string, IQnAQuestionRecord>;
  pendingQuestions?: Record<string, IQnAQuestionRecord>;
}

/**
 * Firebase DB entry of QNA question.
 */
export interface IQnAQuestionRecord {
  anonymous: boolean;
  createdAt: number;
  archivedAt?: number;
  id: string;
  state: EQuestionState;
  text: string;
  user: Optional<IQnAQuestionAuthor>;
  votes: Record<string, any>;
  repliesCount?: Optional<number>;
}

/**
 * Firebase DB entry of QNA question reply.
 */
export interface IQnAQuestionReplyRecord {
  questionId: string;
  createdAt: number;
  text: string;
  votes: Record<string, any>;
  user: IQnAQuestionAuthor;
}

export interface IQnASortingRule {
  type: EQuestionSortType;
  field: EQuestionsSortBy;
  order: ESortOrder;
}

export interface ITabSorting {
  [x: string]: IQnASortingRule;
}

export type TQnAQuestions = Record<string, IQnAQuestion>;

export type TQnAQuestionReplies = Record<string, IQnAQuestionReply>;

export type TQnAQuestionsReplies = Record<string, TQnAQuestionReplies>;

export type TQnAQuestionRepliesRecord = Record<string, IQnAQuestionReplyRecord>;

export type TChatHistory = Record<string, IChatMessage>;

export type TChatParticipants = Record<string, IChatUser>;

export enum EQuestionsSortBy {
  TIMESTAMP = 'createdAt',
  VOTES = 'votesCount',
  ARCHIVED_TIME = 'archivedAt',
}

export enum EQuestionSortType {
  NEWEST = 1,
  OLDEST,
  MOST_POPULAR,
}

export enum ESortOrder {
  ASCENDING = 0,
  DESCENDING = 1,
}

export enum EQnaTab {
  ACTIVE = 'active',
  PENDING = 'pending',
  ARCHIVED = 'archived',
  APPROVED = 'approved',
}

export enum EQuestionState {
  ACTIVE = 'active',
  ASKED = 'asked',
  ANSWERING = 'answering',
  PENDING = 'pending',
  ARCHIVED = 'archived',
}

export enum EPollState {
  DRAFT = 'draft',
  OPENED = 'opened',
  CLOSED = 'closed',
  ARCHIVED = 'archived',
}

export interface IChatBanItem {
  userId: string;
}

export interface IPollVoteItem {
  userId: string;
}

/**
 * Model representation for application usage.
 */
export interface IPollOption {
  id: string;
  text: string;
  correct: boolean;
  isVotedByMe?: boolean;
  votesCount: number;
}

/**
 * Model representation for application usage.
 */
export interface IPoll {
  id: string;
  question: string;
  state: EPollState;
  options: Array<IPollOption>;
  createdAt: number;
  updatedAt?: number;
}

export interface IPollItemActions {
  onPollOpen(poll: IPoll): void;
  onPollClose(poll: IPoll): void;
  onPollArchive(poll: IPoll): void;
  onToggleShowPoll?: Maybe<(poll: IPoll) => void>;
}

/**
 * DB record model stored in firebase.
 */
export interface IPollOptionRecord {
  id: string;
  text: string;
  correct: boolean;
  votes: Record<string, IPollVoteItem>;
}

/**
 * DB record model stored in firebase.
 */
export interface IPollRecord {
  id: string;
  question: string;
  state: EPollState;
  options: Array<IPollOptionRecord>;
  createdAt: number;
  updatedAt?: number;
}

export type TPollsList = Record<string, IPoll>;

export type TChatBanList = Record<string, IChatBanItem>;

export enum EChatType {
  PUBLIC = 1,
  BACKSTAGE = 2,
}

export interface IPollItemMenuActionItem {
  key: string;
  onClick?: (event?: MouseEvent) => void;
  label?: string;
  icon?: ReactNode;
}

export interface IPollPanel {
  id: number;
  label: ReactNode;
  polls: Array<IPoll>;
}

export interface IQnAPanel {
  id: EQnaTab;
  label: ReactNode;
  questions: Array<IQnAQuestion>;
}

export enum EInteractionModule {
  LOGIN = 'login',
  CHAT = 'chat',
  QNA = 'qna',
  POLL = 'poll',
  AUTO = 'auto',
}

export interface IInteractionToolsItemDescriptor {
  id?: EInteractionModule;
  label?: string;
  header?: string;
  headerBackground?: string;
  headerMenu?: ReactNode;
  icon?: ReactNode;
  isClosable?: boolean;
  Content?: ElementType;
}

export interface IChatSessionExportRendererProps extends IBaseDomElementProps {
  onToggleExport: () => void;
  triggerButtonRef: Ref<HTMLButtonElement>;
}

export interface IQnaSessionExportRendererProps extends IBaseDomElementProps {
  onToggleExport: () => void;
}

export interface IPollSessionExportRendererProps extends IBaseDomElementProps {
  onToggleExport: () => void;
}

export interface IActionsMenuItem {
  id: string;
  label: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}
